import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Users, CreditCard, AlertTriangle, Scale, Ban, RefreshCw, Gavel } from "lucide-react";

const Terms = () => {
  const lastUpdated = "February 14, 2025";

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-crimson flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">TERMS OF SERVICE</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-obsidian-light border border-[#222] p-8"
          >
            <p className="text-gray-500 text-sm mb-8">Last Updated: {lastUpdated}</p>

            <div className="prose prose-invert max-w-none space-y-8">
              {/* Agreement */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-crimson" />
                  Agreement to Terms
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Welcome to Rekrutor. These Terms of Service ("Terms") govern your use of our AI-powered recruitment platform. By creating an account or using our services, you agree to be bound by these Terms. If you do not agree, please do not use Rekrutor.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  Rekrutor reserves the right to modify these Terms at any time. We will notify you of material changes via email or platform notification. Continued use after changes constitutes acceptance of the updated Terms.
                </p>
              </section>

              {/* Eligibility */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-crimson" />
                  Eligibility & Account Registration
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  To use Rekrutor, you must:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Not create accounts for others without authorization</li>
                  <li>Not use the platform if previously banned</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  You are responsible for all activities under your account. Notify us immediately of any unauthorized access.
                </p>
              </section>

              {/* User Types */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-crimson" />
                  User Types & Responsibilities
                </h2>
                
                <h3 className="text-lg font-medium text-white mt-4 mb-2">Job Seekers</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Provide accurate and truthful profile information</li>
                  <li>Only apply for jobs you are genuinely interested in</li>
                  <li>Respond professionally to recruiters and interviewers</li>
                  <li>Not misrepresent qualifications, experience, or identity</li>
                </ul>

                <h3 className="text-lg font-medium text-white mt-4 mb-2">Recruiters</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Post legitimate job opportunities only</li>
                  <li>Provide accurate company and job information</li>
                  <li>Comply with employment laws and non-discrimination policies</li>
                  <li>Not use candidate data for purposes other than recruitment</li>
                  <li>Respond to candidates in a timely and professional manner</li>
                </ul>
              </section>

              {/* Prohibited Conduct */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Ban className="w-5 h-5 text-crimson" />
                  Prohibited Conduct
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  You agree NOT to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Post false, misleading, or fraudulent job listings or profiles</li>
                  <li>Harass, discriminate against, or abuse other users</li>
                  <li>Scrape, crawl, or automate access to the platform without permission</li>
                  <li>Attempt to circumvent security measures or access restrictions</li>
                  <li>Use the platform for illegal activities or spam</li>
                  <li>Impersonate others or misrepresent your affiliation</li>
                  <li>Upload malicious content, viruses, or harmful code</li>
                  <li>Interfere with platform operations or other users' access</li>
                  <li>Collect user information for unauthorized purposes</li>
                  <li>Post content that violates intellectual property rights</li>
                </ul>
              </section>

              {/* Subscription & Payments */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-crimson" />
                  Subscriptions & Payments
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Rekrutor offers free and paid subscription tiers:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Free Tier:</strong> Basic access with limited features</li>
                  <li><strong>Plus/Pro Tiers:</strong> Enhanced features, priority support, and additional AI capabilities</li>
                  <li>Subscriptions are billed monthly or annually as selected</li>
                  <li>Payments are processed securely through Stripe</li>
                  <li>Prices may change with 30 days' notice</li>
                </ul>

                <h3 className="text-lg font-medium text-white mt-4 mb-2">Cancellation & Refunds</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Cancel anytime from your account settings</li>
                  <li>Access continues until the end of your billing period</li>
                  <li>Refunds are provided only as required by law or at our discretion</li>
                  <li>Annual subscriptions may qualify for prorated refunds within 14 days</li>
                </ul>
              </section>

              {/* AI Services */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <RefreshCw className="w-5 h-5 text-crimson" />
                  AI-Powered Services
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Rekrutor uses artificial intelligence for various features:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>AI interview recordings and analysis</li>
                  <li>Job matching and recommendations</li>
                  <li>Profile insights and skill gap analysis</li>
                  <li>Job description generation</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  <strong>Disclaimer:</strong> AI features are provided "as is" and may not be 100% accurate. AI-generated content should be reviewed before use. We are not responsible for hiring decisions made based on AI recommendations.
                </p>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Scale className="w-5 h-5 text-crimson" />
                  Intellectual Property
                </h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  <strong>Our Property:</strong> Rekrutor, its logo, design, and features are our intellectual property. You may not copy, modify, or distribute our platform without permission.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  <strong>Your Content:</strong> You retain ownership of content you submit (profiles, resumes, job postings). By posting, you grant Rekrutor a non-exclusive license to use, display, and process your content to provide our services.
                </p>
              </section>

              {/* Disclaimers */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-crimson" />
                  Disclaimers & Limitations
                </h2>
                <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 mb-4">
                  <p className="text-yellow-400 text-sm">
                    REKRUTOR IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE EMPLOYMENT OUTCOMES, JOB PLACEMENT, OR THE ACCURACY OF AI-GENERATED CONTENT.
                  </p>
                </div>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>We do not guarantee you will find a job or candidate</li>
                  <li>We are not responsible for employer or candidate actions</li>
                  <li>Background checks and verification are your responsibility</li>
                  <li>We may suspend or modify services without notice</li>
                </ul>
              </section>

              {/* Limitation of Liability */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Gavel className="w-5 h-5 text-crimson" />
                  Limitation of Liability
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, REKRUTOR SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR OPPORTUNITIES, ARISING FROM YOUR USE OF THE PLATFORM.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim, or $100 if you have not made any payments.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Ban className="w-5 h-5 text-crimson" />
                  Termination
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  You may delete your account at any time. We may suspend or terminate your account if you violate these Terms, engage in prohibited conduct, or for any reason with notice. Upon termination, your right to use Rekrutor ends, but these Terms continue to apply to past use.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2 mb-4">
                  <Scale className="w-5 h-5 text-crimson" />
                  Governing Law & Disputes
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict of law principles. Any disputes shall be resolved through binding arbitration, except where prohibited by law. You waive the right to participate in class action lawsuits.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-xl font-heading font-bold text-white mb-4">Contact Us</h2>
                <p className="text-gray-300 leading-relaxed">
                  For questions about these Terms:
                </p>
                <div className="mt-4 p-4 bg-obsidian border border-[#333]">
                  <p className="text-white font-medium">Rekrutor Legal Team</p>
                  <p className="text-gray-400">Email: legal@rekrutor.com</p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#222] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex justify-between items-center">
          <p className="text-gray-500 text-sm">© 2025 Rekrutor. All rights reserved.</p>
          <div className="flex gap-4 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-crimson">Privacy</Link>
            <Link to="/support" className="text-gray-400 hover:text-crimson">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
